/** @jsxImportSource @emotion/react */

import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchActivePlan } from 'shared/hooks/fetchering/use-fetch-active-plan';

import { AppDispatch } from 'store/store';
import { UDText } from 'ud-ui/components';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { UpsertLocationInput } from 'modules/locations/domain/interfaces/actions';
import { fetchLocation, upsertLocation } from 'modules/locations/store/actions';
import { selectLocation, selectLocations, selectProcess } from 'modules/locations/store/selectors';
import LocationForm from '../components/location-form';
import useFetchMapImage from '../hooks/use-fetch-map-image';

function LocationViewPage() {
  const dispatch = useDispatch<AppDispatch>();
  const tournamentId = useSelector(authStoreUserTournamentId);
  const locations = useSelector(selectLocations);
  const navigate = useNavigate();
  const location = useSelector(selectLocation(Number(useParams().id)));
  const process = useSelector(selectProcess);
  const { setHeaderContent } = useMainLayout();
  const { data: activePlan, isFetching: isPlanFetching } = useFetchActivePlan();

  const id = Number(useParams().id);
  if (isNaN(id)) {
    navigate(Pages.LOCATIONS.INDEX);
  }

  const { isLoading: isMapImageLoading } = useFetchMapImage({ locationId: id });

  // Сначала ищем локацию в сторе
  const storedLocation = useMemo(() => locations.find((loc) => loc.id === id), [locations, id]);

  useEffect(() => {
    if (!storedLocation) {
      dispatch(fetchLocation({ id }));
    }
  }, [dispatch, id, storedLocation]);

  const handleSubmit = useCallback(
    async (values: UpsertLocationInput) => {
      if (!tournamentId) {
        console.error('No tournament selected');
        return;
      }

      try {
        const locationData: UpsertLocationInput = {
          ...values,
          tournamentId,
        };

        await dispatch(upsertLocation(locationData)).unwrap();
      } catch (error) {
        console.error('Failed to save location:', error);
      }
    },
    [dispatch, tournamentId],
  );

  const breadcrumbs = useMemo(() => {
    return [
      { label: 'Локации', to: Pages.LOCATIONS.INDEX },
      {
        label: location?.name ?? 'Неизвестная локация',
        to: Pages.LOCATIONS.builders.edit(id),
      },
    ];
  }, [id, location?.name]);

  const breadcrumb = useMemo(() => <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbs} />, [breadcrumbs]);

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [breadcrumb, setHeaderContent]);

  if (isPlanFetching || (process.isProcessing && process.name === 'fetchLocation')) {
    return (
      <>
        <div className="w-full px-6 pt-6">
          <div className="skeleton-block" style={{ height: '200px' }} />
        </div>
      </>
    );
  }

  if (!location) {
    return (
      <>
        <div className="w-full px-6 pt-6">
          <div className="flex-center mt-24 flex flex-col">
            <div className="text body-text bold mb-2">Такая локация не найдена</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="w-full px-6 py-6">
        <UDText type="headline" className="mr-10">
          Локация для проведения турнира
        </UDText>
      </div>
      <div className="border-surfacePlatinum20 flex w-full items-center border-b px-6" />
      <div className="w-full px-6 pt-6">
        <LocationForm
          location={location}
          isLoading={process.isProcessed && process.name === 'fetchLocation'}
          onSubmit={handleSubmit}
          action="edit"
          isAvailableFeatures={Boolean(activePlan)}
          isMapImageLoading={isMapImageLoading}
        />
      </div>
    </>
  );
}

export default LocationViewPage;
